import { isEmpty, size } from "lodash";
import { createSelector } from "reselect";

import type { PollService } from "@busbud/core-types";
import type {
  RelatedPublicSegment,
  RelatedPublicSegmentV4,
  RelatedPublicTrip,
  RelatedPublicTripV4
} from "@busbud/core-types/dist/lib/poll-service/api/udm";

import type { DisplayedPriceConfig } from "@app/helpers/price";
import { isReturn } from "@app/helpers/search";
import { removeTimeOffset } from "@app/helpers/time";
import type { Direction } from "@app/helpers/udm/get-display-price";
import { getDisplayPrice } from "@app/helpers/udm/get-display-price";
import type { NapiSource } from "@app/types/napi-types";

import { getLegCardHashId } from "./get-leg-cards";
import {
  getLegSourceNamesFromTripPrices,
  getSearchedLeg,
  getUniqueSourceNamesFromSegments
} from "../../../../helpers/udm";
import { boxWhenNecessaryStrategy } from "../../utils/binning/binning-strategies";
import { getDisplayedPriceConfig } from "../get-displayed-price-config";
import {
  getConfig,
  getSearch,
  getSources,
  getTrips,
  getTripsLocations,
  getTripsSegments
} from "../state-accessors";

type TripsMap =
  | Record<string, RelatedPublicTrip>
  | Record<string, RelatedPublicTripV4>;
type LegCardDataMap = Record<string, FormattedLegCardData>;
type Locations = Record<string, PollService.API.RelatedPublicStop>;

type LegFormatData = {
  trip: RelatedPublicTrip | RelatedPublicTripV4;
  trip_segments: (RelatedPublicSegment | RelatedPublicSegmentV4)[];
  source: NapiSource | undefined;
  locations: Locations;
  direction: Direction;
  displayed_price_config: DisplayedPriceConfig;
};
type FormattedLegCardData = {
  id: string;
  departure_time: string;
  source_name: string | undefined;
  price_in_cents: number;
  origin_id: string;
  destination_id: string;
};
type CurateTripsResult = {
  curated_trips: TripsMap;
  excluded_leg_card_ids: string[];
  excluded_trips_count: number;
};

const formatLegCardForExclusion = (
  leg_card_id: string,
  data: LegFormatData
): FormattedLegCardData => {
  const {
    direction,
    locations,
    source,
    trip_segments,
    trip,
    displayed_price_config
  } = data;
  const first_segment = trip_segments[0];
  const last_segment = trip_segments[trip_segments.length - 1];
  const source_name = source && source.name;

  const departure_time = removeTimeOffset(
    // @ts-expect-error TS18048 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
    first_segment.departure_time.timestamp
  );

  // @ts-expect-error TS18048 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
  const origin_id = locations[first_segment.origin_id]?.city_id || "";
  // @ts-expect-error TS18048 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
  const destination_id = locations[last_segment.destination_id]?.city_id || "";
  const price_in_cents = getDisplayPrice(
    trip,
    direction,
    displayed_price_config
  );

  return {
    id: leg_card_id,
    departure_time,
    source_name,
    price_in_cents,
    origin_id,
    destination_id
  };
};

const getFormattedLegCards = createSelector(
  [
    getConfig,
    getSearch,
    getTrips,
    getTripsSegments,
    getSources,
    getTripsLocations,
    getDisplayedPriceConfig
  ],
  (
    config,
    search,
    trips,
    segments,
    sources,
    locations,
    displayed_price_config
  ): LegCardDataMap => {
    if (!search || !trips) {
      return {};
    }

    const direction = isReturn(search) ? "return" : "outbound";

    return Object.keys(trips).reduce((acc, trip_id) => {
      const trip = trips[trip_id];

      // @ts-expect-error TS2345 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
      const leg = getSearchedLeg(search, trip);
      const trip_segments = leg.segment_ids.map(
        segment_id => segments[segment_id]
      );

      const trip_prices_source_names = getLegSourceNamesFromTripPrices(
        leg,
        trip
      );
      const cart_trip_segment_source_names = getUniqueSourceNamesFromSegments(
        trip_segments as unknown as { source_name: string }[]
      );
      const source_name = (
        trip_prices_source_names.length === 0
          ? cart_trip_segment_source_names
          : trip_prices_source_names
      ).join(" & ");

      const source = sources.find(source => source.name === source_name);

      const leg_card_id = getLegCardHashId(
        // @ts-expect-error TS2345 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
        trips[trip_id],
        segments,
        search,
        config.experiments
      );
      const leg_card_data = formatLegCardForExclusion(leg_card_id, {
        // @ts-expect-error TS2322 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
        trip,
        // @ts-expect-error TS2322 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
        trip_segments,
        direction,
        source,
        locations,
        displayed_price_config
      });

      acc[trip_id] = leg_card_data;
      return acc;
    }, {} as LegCardDataMap);
  }
);

const filterTripsWithExcludedLegCardIds = (
  trips: TripsMap,
  excluded_leg_card_ids: string[],
  legs_data_map: LegCardDataMap
): TripsMap => {
  return Object.keys(trips).reduce((acc, trip_id) => {
    const trip = trips[trip_id];
    const trip_legs = legs_data_map[trip_id];
    // @ts-expect-error TS18048 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
    const leg_card_id = trip_legs.id;
    if (!excluded_leg_card_ids.includes(leg_card_id)) {
      // @ts-expect-error TS2322 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
      acc[trip_id] = trip;
    }
    return acc;
  }, {} as TripsMap);
};

const findCuratedTripData = (
  trips: TripsMap,
  legs_data_map: LegCardDataMap
) => {
  const legs_data = Object.values(legs_data_map);
  const excluded_leg_card_ids = boxWhenNecessaryStrategy(legs_data);
  const curated_trips = filterTripsWithExcludedLegCardIds(
    trips,
    excluded_leg_card_ids,
    legs_data_map
  );

  return {
    curated_trips: curated_trips,
    excluded_leg_card_ids,
    excluded_trips_count: size(trips) - size(curated_trips)
  };
};

export const getCuratedTrips = createSelector(
  [getTrips, getFormattedLegCards],
  (trips, legs_data_map): CurateTripsResult => {
    const result = {
      curated_trips: trips,
      excluded_leg_card_ids: [] as string[],
      excluded_trips_count: 0
    } as CurateTripsResult;

    if (isEmpty(trips) || isEmpty(legs_data_map)) {
      return result;
    }

    const { curated_trips, excluded_leg_card_ids, excluded_trips_count } =
      findCuratedTripData(trips, legs_data_map);
    result.curated_trips = curated_trips;
    result.excluded_leg_card_ids = excluded_leg_card_ids;
    result.excluded_trips_count = excluded_trips_count;

    return result;
  }
);
